import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faCheck,
  faXmark,
  faRotateLeft,
  faUserPlus,
  faBolt,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  inspectionService,
  type OwnershipAction,
  type OwnershipHistoryItem,
} from '@/services/inspection.service';

interface OwnershipHistoryPanelProps {
  inspectionId: string;
}

const ACTION_META: Record<OwnershipAction, { label: string; icon: IconDefinition; color: string }> =
  {
    TRANSFER_INITIATED: {
      label: 'Transfer Initiated',
      icon: faArrowRightArrowLeft,
      color: 'text-amber-500',
    },
    TRANSFER_ACCEPTED: {
      label: 'Transfer Accepted',
      icon: faCheck,
      color: 'text-green-500',
    },
    TRANSFER_REJECTED: {
      label: 'Transfer Rejected',
      icon: faXmark,
      color: 'text-red-500',
    },
    TRANSFER_REVOKED: {
      label: 'Transfer Revoked',
      icon: faRotateLeft,
      color: 'text-orange-500',
    },
    TAKEOVER_REQUESTED: {
      label: 'Takeover Requested',
      icon: faUserPlus,
      color: 'text-blue-500',
    },
    TAKEOVER_ACCEPTED: {
      label: 'Takeover Accepted',
      icon: faCheck,
      color: 'text-green-500',
    },
    TAKEOVER_REJECTED: {
      label: 'Takeover Rejected',
      icon: faXmark,
      color: 'text-red-500',
    },
    FORCE_TRANSFER: {
      label: 'Force Transfer',
      icon: faBolt,
      color: 'text-destructive',
    },
  };

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function HistoryEntry({ entry }: { entry: OwnershipHistoryItem }) {
  const meta = ACTION_META[entry.action];

  return (
    <div className="relative flex gap-4">
      {/* timeline spine */}
      <div className="flex flex-col items-center">
        <span
          className={`flex h-7 w-7 items-center justify-center rounded-full bg-background border-2 border-border shrink-0 ${meta.color}`}
        >
          <FontAwesomeIcon icon={meta.icon} className="h-3 w-3" />
        </span>
        <div className="mt-1 w-px flex-1 bg-border" />
      </div>

      <div className="pb-6 flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-semibold text-foreground">{meta.label}</span>
          <span className="text-xs text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
        </div>

        <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
          {entry.initiatorName && (
            <p>
              By: <span className="font-medium text-foreground">{entry.initiatorName}</span>
            </p>
          )}
          {entry.oldOwnerName && entry.newOwnerName && (
            <p>
              Ownership: <span className="font-medium text-foreground">{entry.oldOwnerName}</span>
              {' → '}
              <span className="font-medium text-foreground">{entry.newOwnerName}</span>
            </p>
          )}
          {entry.oldOwnerName && !entry.newOwnerName && (
            <p>
              Previous owner:{' '}
              <span className="font-medium text-foreground">{entry.oldOwnerName}</span>
            </p>
          )}
          {!entry.oldOwnerName && entry.newOwnerName && (
            <p>
              New owner: <span className="font-medium text-foreground">{entry.newOwnerName}</span>
            </p>
          )}
        </div>

        {entry.reason && (
          <p className="mt-1.5 text-xs text-foreground/80 italic">Reason: {entry.reason}</p>
        )}

        {entry.comment && (
          <p className="mt-1 text-xs text-muted-foreground">Note: {entry.comment}</p>
        )}
      </div>
    </div>
  );
}

export function OwnershipHistoryPanel({ inspectionId }: OwnershipHistoryPanelProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inspection-ownership-history', inspectionId],
    queryFn: () => inspectionService.getOwnershipHistory(inspectionId),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <FontAwesomeIcon icon={faCircleNotch} className="h-4 w-4 animate-spin" />
        Loading ownership history…
      </div>
    );
  }

  if (isError) {
    return <p className="py-4 text-sm text-destructive">Failed to load ownership history.</p>;
  }

  if (!data?.data?.length) {
    return (
      <p className="py-4 text-sm text-muted-foreground italic">
        No ownership changes recorded for this inspection.
      </p>
    );
  }

  return (
    <div className="pt-2">
      {data.data.map((entry, idx) => (
        <div
          key={entry.id}
          className={idx === data.data.length - 1 ? '[&_.flex-1.w-px]:hidden' : ''}
        >
          <HistoryEntry entry={entry} />
        </div>
      ))}
    </div>
  );
}
